import styled, { css, down } from "@xstyled/styled-components";
import { useQuery } from "@apollo/client";
import { Navigate } from "react-router-dom";
import {
  PaperplaneIcon,
  CelebrateIcon,
  OfferStatusIcon,
} from "@welcome-ui/icons";

import { Card, Heading, Spacing, Text, responsive } from "@otta/design";
import { pxToRem } from "@otta/design-tokens";
import { Loading } from "@otta/shared-components";
import { LogoHeader, useProgress } from "@hire/components/layout/QuizLayout";
import {
  CatalogueProductId,
  CurrentUserDocument,
  ProductQuoteDocument,
} from "@hire/schema";
import { VideoContent } from "@hire/components/VideoContent";
import { formatCurrency } from "@hire/util/currencies";

const PageWrapper = styled.div`
  display: flex;
  padding: 80 xxl;
  flex-direction: column;
  align-items: center;

  ${down(
    "tablet",
    css`
      padding: 40 lg;
      gap: 40;
    `
  )}
`;

const Main = styled.main`
  background-color: beige-200;
  min-height: 100vh;
`;

const CardContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: xl;
  max-width: ${pxToRem(1376)};

  ${down(
    "tablet",
    css`
      grid-template-columns: 1fr;
      gap: lg;
    `
  )}
`;

const StyledCard = styled(Card)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4xl;
  padding: 40 xl 4xl;
  border-radius: lg;
  text-align: center;

  ${down(
    "tablet",
    css`
      gap: lg;
      padding: xl lg xxl;
    `
  )}
`;

const StatusTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: lg;
  align-self: stretch;
`;

const VideoContainer = styled.div`
  min-width: 50vw;
`;

const CardTitle = styled(Heading)`
  ${responsive`font-size: ${responsive.modularScale({
    mobile: 1,
    desktop: 3,
  })};`}
`;

export function Success() {
  useProgress(0);
  const { data, loading } = useQuery(CurrentUserDocument);

  const { data: productQuoteData, loading: productQuoteLoading } = useQuery(
    ProductQuoteDocument,
    {
      variables: {
        productId: CatalogueProductId.EssentialMonthly,
        quantity: 1,
      },
    }
  );

  const essentialMonthlyValue = productQuoteData?.catalogue?.product?.quote
    ? formatCurrency(
        productQuoteData.catalogue.product.quote.netTotal,
        productQuoteData.catalogue.product.quote.currency
      )
    : "0";

  if (loading || productQuoteLoading) {
    return <Loading />;
  }

  if (
    !data?.me ||
    data?.me?.__typename !== "CurrentCompanyRecruiter" ||
    !data.me.currentCompany
  ) {
    return <Navigate to="/quiz" />;
  }

  return (
    <Main>
      <LogoHeader />
      <PageWrapper>
        <Spacing size={5}>
          <Heading size={5}>
            Our Company Specialists are busy creating your company profile.
          </Heading>
          <Heading size={3}>In the meantime please…</Heading>

          <CardContainer>
            <StyledCard>
              <PaperplaneIcon w={{ _: 80, tablet: 100 }} />
              <StatusTextContainer>
                <CardTitle>1. Verify your email</CardTitle>
                <Text>
                  Click the link in the email we sent you to be able to sign in.
                </Text>
              </StatusTextContainer>
            </StyledCard>
            <StyledCard>
              <CelebrateIcon w={{ _: 80, tablet: 100 }} />
              <StatusTextContainer>
                <CardTitle>2. Create a job</CardTitle>
                <Text>
                  Once you've signed in, via the email we've sent, you can
                  either post a job directly or link your ATS and we will
                  publish your jobs.
                </Text>
              </StatusTextContainer>
            </StyledCard>
            <StyledCard>
              <OfferStatusIcon w={{ _: 80, tablet: 100 }} />
              <StatusTextContainer>
                {essentialMonthlyValue === "0" ? (
                  <CardTitle>3. Purchase your plan</CardTitle>
                ) : (
                  <CardTitle>
                    3. Purchase your plan for {essentialMonthlyValue}
                    /month
                  </CardTitle>
                )}
                <Text>On a monthly rolling subscription.</Text>
              </StatusTextContainer>
            </StyledCard>
          </CardContainer>
          <VideoContainer>
            <VideoContent
              title="What is happening now?"
              videoUrl="https://vimeo.com/1101495153"
              videoAnalyticsCategory="Sign-up Quiz - Success"
            />
          </VideoContainer>
        </Spacing>
      </PageWrapper>
    </Main>
  );
}
